import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PlusOneIcon from '@mui/icons-material/PlusOne';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { logout } from 'API/account';
import { Roles, Routes } from 'enums';
import { useExitSudo } from 'hooks/use-exit-sudo';
import { useExtensionAuth } from 'hooks/use-extension-auth';
import { useUserData } from 'hooks/use-user-data';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useBoosterToolsGenreStore from 'stores/genre-store';
import useUserStore from 'stores/user-store';
import { makeStyles } from 'tss-react/mui';
import AdminReloginDialog from '../sidebar/admin-relogin-dialog';

interface Props {
  anchorEl: HTMLElement | null;

  onClose(): void;
}

const AccountMenu: React.FC<Props> = ({ anchorEl, onClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { firstName, lastName, email, role } = useUserData();
  const signOut = useUserStore((state) => state.signOut);
  const { extensionSignOut } = useExtensionAuth();
  const resetAllGenres = useBoosterToolsGenreStore((state) => state.resetAllGenres);
  const { exitImpersonation } = useExitSudo();

  const [adminReloginDialogOpen, setAdminReloginDialogOpen] = useState(false);

  const canUpgrade = role === Roles.ADMIN || role === Roles.MANAGER || role === Roles.AGENCY || role === Roles.OWNER;

  const go = (route: string) => {
    onClose();
    navigate(route);
  };

  async function handleLogout() {
    onClose();
    const storedAdminEmail = localStorage.getItem('adminEmail');

    if (storedAdminEmail) {
      // Impersonation is running: return to the admin's own account instead of
      // logging out. Only if that exchange fails do we ask for the password.
      const restored = await exitImpersonation();
      if (restored) {
        window.location.reload();
        return;
      }
      setAdminReloginDialogOpen(true);
    } else {
      await logout();
      resetAllGenres();
      signOut();
      localStorage.removeItem('rememberLinkedinAccount');
      extensionSignOut();
    }
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.header}>
          <Typography className={classes.name}>{`${firstName} ${lastName}`.trim() || t('account')}</Typography>
          <Typography className={classes.email}>{email}</Typography>
        </div>
        <Divider className={classes.headerDivider} />
        <MenuItem className={classes.menuItem} onClick={() => go(Routes.ACCOUNT)}>
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('account')}</ListItemText>
        </MenuItem>
        {canUpgrade ? (
          <MenuItem className={classes.menuItem} onClick={() => go(Routes.UPGRADE)}>
            <ListItemIcon>
              <PlusOneIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.upgradeText }}>{t('upgrade')}</ListItemText>
          </MenuItem>
        ) : null}
        <Divider />
        <MenuItem className={classes.menuItem} onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('logout')}</ListItemText>
        </MenuItem>
      </Menu>

      <AdminReloginDialog open={adminReloginDialogOpen} handleClose={() => setAdminReloginDialogOpen(false)} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: '244px',
    borderRadius: '10px',
    border: '1px solid #E3E3E8',
    boxShadow: '0 4px 12px 0 rgba(0,0,0,0.18)',
    marginLeft: '10px',
  },
  header: {
    padding: '8px 16px 10px 16px',
  },
  headerDivider: {
    marginBottom: '4px',
  },
  name: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
  },
  email: {
    fontSize: theme.typography.pxToRem(11.5),
    color: theme.palette.greys?.medium ?? '#707070',
  },
  menuItem: {
    fontSize: theme.typography.pxToRem(13),
    borderRadius: '6px',
    margin: '0 8px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  upgradeText: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

export default AccountMenu;
